/**
 * Frente IDSS — as regras públicas do IDSS (fichas técnicas da ANS) aplicadas
 * na nossa carteira e nas contas médicas: indicador a indicador do IDQS e do
 * IDGA, com as fatias (idade, sexo, plano) e a lista de quem falta — o ponto de
 * partida das campanhas. É estimativa: a nota oficial sai só da ANS.
 */
import React, { useEffect, useState } from "react";
import { ChevronRight, ChevronDown, Download, RefreshCw, Users } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../services/api";
import { T, MONO, InfoTip, Card, Botao, TH, TD, fmtInt } from "./ui";

const num = (v, casas = 2) => (v === null || v === undefined ? "—"
  : Number(v).toLocaleString("pt-BR", { minimumFractionDigits: casas, maximumFractionDigits: casas }));
const dataBr = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const faixaNota = (v) => (v === null || v === undefined ? { bg: "#f3f4f6", fg: T.inkSoft }
  : v >= 0.8 ? { bg: "#d1f2e0", fg: "#11643b" } : v >= 0.6 ? { bg: "#e6f4d7", fg: "#3c6e14" }
  : v >= 0.4 ? { bg: "#fff4cf", fg: "#7a5a00" } : v >= 0.2 ? { bg: "#fde2cc", fg: "#8a3d06" } : { bg: "#fbd5d5", fg: "#8e1b1b" });

const TIP_FRENTE = "As regras do IDSS são públicas: a ANS publica, para cada indicador, uma ficha técnica com o público (idade, sexo), os procedimentos que contam, o profissional e a conta que vira nota de 0 a 1. Aqui essas regras rodam na nossa carteira ativa e nas contas médicas. Dá para ver onde estamos, em qual fatia e quem falta. É uma estimativa: a nota oficial usa os dados do ano inteiro enviados à ANS (TISS e SIB), a média de beneficiários do ano e ajustes por idade e sexo.";
const TIP_PARCIAL = "Média ponderada pelo peso, como a ANS faz, mas só com os indicadores calculados aqui. Serve para ver a direção; a nota da dimensão tem os demais indicadores e as pontuações extras.";
const TIP_POTENCIAL = "Peso do indicador × quanto falta para a nota máxima (1). Quanto maior, mais a melhora neste indicador puxa a dimensão para cima. É por onde começar.";
const TIP_NOTA = "Nota estimada de 0 a 1 pela régua da ficha técnica: abaixo do piso vale 0, a partir da meta vale 1, e entre os dois cresce em linha reta.";

function Nota({ v }) {
  const c = faixaNota(v);
  return <span style={{ fontFamily: MONO, fontWeight: 700, background: c.bg, color: c.fg, borderRadius: 6, padding: "2px 8px" }}>{num(v, 2)}</span>;
}

function baixarCsv(codigo, dados) {
  const temFac = dados.pessoas.some((p) => p.facilidade);
  const cab = ["Matrícula", "Nome", "Idade", "Sexo", "Plano", "Contato", "Situação", ...(temFac ? ["Facilidade", "Por quê"] : [])];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const linhas = dados.pessoas.map((p) => [p.matricula, p.nome, p.idade, p.sexo, p.plano, p.contato, p.detalhe,
    ...(temFac ? [p.facilidade, p.facilidade_motivo] : [])].map(esc).join(";"));
  const blob = new Blob(["﻿" + [cab.map(esc).join(";"), ...linhas].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `idss_${codigo.replace(".", "_")}_pendentes.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const COR_FAC = { Alta: ["#d1f2e0", "#11643b"], "Média": ["#fff4cf", "#7a5a00"], Baixa: ["#eef1f4", "#55606b"] };
function Fac({ v }) {
  if (!v) return null;
  const [bg, fg] = COR_FAC[v] || COR_FAC.Baixa;
  return <span style={{ background: bg, color: fg, borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>{v}</span>;
}

// Lista de pessoas (a da campanha). `url` troca a origem — o Painel usa para os críticos.
export function Pendentes({ codigo, periodo, fatia, valor, titulo, url }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    setD(null);
    api.get(url || `/indicadores-ans/base/${codigo}/pendentes`, { params: url ? {} : { periodo, fatia, valor } })
      .then((r) => { if (vivo) setD(r.data); })
      .catch(() => { if (vivo) { setD({ pessoas: [], total: 0 }); toast.error("Falha ao carregar a lista"); } });
    return () => { vivo = false; };
  }, [codigo, periodo, fatia, valor, url]);
  if (!d) return <p style={{ fontSize: 12, color: T.inkSoft }}>Carregando a lista…</p>;
  const mostrar = d.pessoas.slice(0, 200);
  return (
    <div style={{ marginTop: 8, border: `1px solid ${T.line}`, borderRadius: 8, padding: 10, background: "#fcfcfd" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <Users size={14} color={T.teal} />
        <b style={{ fontSize: 13 }}>{titulo || d.titulo}</b>
        <span style={{ fontSize: 12, color: T.inkSoft }}>{fmtInt(d.total)} pessoa(s)</span>
        <InfoTip w={320} texto="Beneficiários ativos do público deste indicador que ainda não foram alcançados no período. É a lista para campanha: contato ativo, agendamento, convite. Baixe o CSV para trabalhar fora, ou use a lista para criar as tarefas." />
        {d.por_facilidade && (
          <span style={{ fontSize: 12, color: T.inkSoft, display: "inline-flex", alignItems: "center", gap: 6 }}>
            {["Alta", "Média", "Baixa"].map((k) => (
              <span key={k}><Fac v={k} /> {fmtInt(d.por_facilidade[k] || 0)}</span>
            ))}
            <InfoTip w={340} texto={d.ajuda_facilidade} />
          </span>
        )}
        {d.total > 0 && (
          <div style={{ marginLeft: "auto" }}>
            <Botao pequeno onClick={() => baixarCsv(codigo, d)}><Download size={13} /> Baixar CSV{d.total > d.pessoas.length ? ` (${fmtInt(d.pessoas.length)} primeiras)` : ""}</Botao>
          </div>
        )}
      </div>
      {d.total === 0 ? <p style={{ fontSize: 12, color: T.inkSoft, margin: 0 }}>Ninguém pendente nesta seleção.</p> : (
        <div style={{ maxHeight: 320, overflow: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr>
              {[...(d.por_facilidade ? ["Facilidade"] : []), "Nome", "Matrícula", "Idade", "Plano", "Contato", "Situação"].map((h) => <th key={h} style={{ ...TH, textAlign: "left" }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {mostrar.map((p) => (
                <tr key={p.matricula}>
                  {d.por_facilidade && <td style={TD} title={p.facilidade_motivo}><Fac v={p.facilidade} /></td>}
                  <td style={TD}>{p.nome}</td>
                  <td style={{ ...TD, fontFamily: MONO, fontSize: 12 }}>{p.matricula}</td>
                  <td style={TD}>{p.idade}</td>
                  <td style={{ ...TD, fontSize: 12 }}>{p.plano}</td>
                  <td style={{ ...TD, fontSize: 12 }}>{p.contato || "—"}</td>
                  <td style={{ ...TD, fontSize: 12, color: T.inkSoft }}>{p.detalhe}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {d.total > mostrar.length && <p style={{ fontSize: 11, color: T.inkSoft, margin: "6px 0 0" }}>Mostrando {mostrar.length} de {fmtInt(d.total)}. O CSV traz a lista completa.</p>}
        </div>
      )}
    </div>
  );
}

function Detalhe({ ind, periodo }) {
  const [lista, setLista] = useState(null);   // {fatia, valor, titulo} | {} (todas)
  const casas = ind.casas ?? 2;
  return (
    <div style={{ padding: "10px 12px 14px", background: "#fafbfc", borderBottom: `1px solid ${T.line}` }}>
      <div style={{ fontSize: 13, color: T.ink, marginBottom: 6 }}>
        {ind.nota >= 1
          ? <>Meta atingida no período. Manter o ritmo.</>
          : <>Faltam <b>{fmtInt(ind.faltam)}</b> {ind.faltam_txt}.</>}
        {" "}<span style={{ color: T.inkSoft }}>Conta: {fmtInt(ind.numerador)} ÷ {num(ind.denominador, Number.isInteger(ind.denominador) ? 0 : 1)} = {num(ind.resultado, casas)} {ind.unidade}.</span>
      </div>
      <ul style={{ margin: "0 0 10px", paddingLeft: 18, fontSize: 11, color: T.inkSoft }}>
        {(ind.ressalvas || []).map((r) => <li key={r}>{r}</li>)}
      </ul>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {Object.entries(ind.fatias || {}).map(([nome, fs]) => (
          <div key={nome} style={{ flex: "1 1 320px", minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Por {nome.toLowerCase()}</div>
            <div style={{ maxHeight: 260, overflow: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%" }}>
                <thead><tr>
                  <th style={{ ...TH, textAlign: "left" }}>{nome}</th>
                  <th style={{ ...TH, textAlign: "right" }}>Público</th>
                  {ind.codigo === "1.6"
                    ? <><th style={{ ...TH, textAlign: "right" }}>Fez 1</th><th style={{ ...TH, textAlign: "right" }}>Fez 2+</th></>
                    : <th style={{ ...TH, textAlign: "right" }}>Resultado</th>}
                  <th style={{ ...TH, textAlign: "right" }}>Pendentes</th>
                </tr></thead>
                <tbody>
                  {fs.map((f) => (
                    <tr key={f.rotulo}>
                      <td style={{ ...TD, fontSize: 12 }}>{f.rotulo}</td>
                      <td style={{ ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12 }}>{fmtInt(f.denominador)}</td>
                      {ind.codigo === "1.6"
                        ? <><td style={{ ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12 }}>{fmtInt(f.com_1)}</td>
                            <td style={{ ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12 }}>{fmtInt(f.com_2_ou_mais)}</td></>
                        : <td style={{ ...TD, textAlign: "right", fontFamily: MONO, fontSize: 12,
                              color: f.resultado !== null && f.resultado < ind.meta ? T.risco : T.ink }}>{num(f.resultado, casas)}</td>}
                      <td style={{ ...TD, textAlign: "right" }}>
                        {f.pendentes > 0
                          ? <button onClick={() => setLista({ fatia: nome, valor: f.rotulo, titulo: `${ind.pendentes_txt} · ${f.rotulo}` })}
                              style={{ border: "none", background: "none", color: T.azul, cursor: "pointer", fontFamily: MONO, fontSize: 12, textDecoration: "underline" }}>{fmtInt(f.pendentes)}</button>
                          : <span style={{ fontFamily: MONO, fontSize: 12, color: T.inkSoft }}>0</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <Botao pequeno onClick={() => setLista({})}><Users size={13} /> {ind.pendentes_txt} ({fmtInt(ind.pendentes)})</Botao>
      </div>
      {lista && <Pendentes codigo={ind.codigo} periodo={periodo} fatia={lista.fatia} valor={lista.valor} titulo={lista.titulo} />}
    </div>
  );
}

const TIP_NOTA_ANS = "Nota oficial publicada pela ANS para a nossa operadora no programa mais recente (dados abertos do IDSS). É a nota que vale; a 'parcial' ao lado é a conta feita aqui só com os indicadores que dá para calcular nas contas médicas.";
const TIP_ALEM = "Cuidados que acompanhamos pela regra do Ministério da Saúde/INCA, mesmo sem estarem no IDSS. Não têm nota da ANS nem entram na conta das dimensões. Servem para achar quem precisa de cuidado e montar campanhas.";

// Cabeçalho de dimensão que abre e fecha (a Frente não fica poluída: só a
// dimensão escolhida mostra os indicadores).
function Dimensao({ chave, dim, itens, periodo }) {
  const [aberta, setAberta] = useState(false);
  const podeAbrir = dim.itens && itens.length > 0;
  return (
    <div style={{ marginTop: 10, border: `1px solid ${T.line}`, borderRadius: 10 }}>
      <div onClick={() => podeAbrir && setAberta(!aberta)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", flexWrap: "wrap", cursor: podeAbrir ? "pointer" : "default", background: aberta ? "#fafbfc" : "#fff", borderRadius: 10 }}>
        {podeAbrir ? (aberta ? <ChevronDown size={16} /> : <ChevronRight size={16} />) : <span style={{ width: 16 }} />}
        <b style={{ fontSize: 14, color: T.ink }}>{chave.toUpperCase()} · {dim.rotulo}</b>
        <span style={{ fontSize: 11, color: T.inkSoft }}>peso {Math.round(dim.peso_idss * 100)}% no IDSS</span>
        {dim.ajuda && <InfoTip w={360} texto={dim.ajuda} />}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: T.inkSoft }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>nota ANS <Nota v={dim.nota_ans} /><InfoTip w={320} texto={TIP_NOTA_ANS} /></span>
          {dim.itens && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              calculada aqui ({dim.calculados} de {dim.total}) <Nota v={dim.nota_parcial} /><InfoTip w={320} texto={TIP_PARCIAL} />
            </span>
          )}
        </div>
      </div>
      {aberta && <div style={{ padding: "0 12px 12px" }}><TabelaDimensao itens={itens} periodo={periodo} /></div>}
    </div>
  );
}

function TabelaDimensao({ itens, periodo, alem }) {
  const [aberto, setAberto] = useState(null);
  const maxGanho = Math.max(0.001, ...itens.filter((i) => i.ganho !== null && i.ganho !== undefined).map((i) => i.ganho));
  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...TH, textAlign: "left", position: "static" }}>Indicador</th>
            {!alem && <th style={{ ...TH, textAlign: "center", position: "static" }}>Peso</th>}
            <th style={{ ...TH, textAlign: "left", position: "static" }}>Público</th>
            <th style={{ ...TH, textAlign: "right", position: "static" }}>Resultado</th>
            <th style={{ ...TH, textAlign: "right", position: "static" }}>Meta</th>
            {alem ? <th style={{ ...TH, textAlign: "right", position: "static" }}>Faltam para a meta</th> : <>
              <th style={{ ...TH, textAlign: "center", position: "static" }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>Nota<InfoTip w={300} texto={TIP_NOTA} /></span>
              </th>
              <th style={{ ...TH, textAlign: "left", position: "static", minWidth: 120 }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>Potencial<InfoTip w={300} texto={TIP_POTENCIAL} /></span>
              </th>
            </>}
          </tr></thead>
          <tbody>
            {itens.map((i) => {
              const abre = i.calculado;
              const esta = aberto === i.codigo;
              return (
                <React.Fragment key={i.codigo}>
                  <tr onClick={() => abre && setAberto(esta ? null : i.codigo)}
                      style={{ cursor: abre ? "pointer" : "default", opacity: i.calculado ? 1 : 0.55 }}>
                    <td style={TD}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {abre ? (esta ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span style={{ width: 14 }} />}
                        <span style={{ fontFamily: MONO, fontSize: 12, color: T.inkSoft }}>{i.codigo}</span>
                        <span style={{ fontWeight: i.calculado ? 600 : 400 }}>{i.nome}</span>
                        <InfoTip w={380} texto={i.regra + (i.calculado ? "" : ` ${i.motivo}`)} />
                      </span>
                    </td>
                    {!alem && <td style={{ ...TD, textAlign: "center", fontSize: 12 }}>{i.peso ?? <span style={{ color: T.inkSoft }}>{i.peso_txt}</span>}</td>}
                    <td style={{ ...TD, fontSize: 12, color: T.inkSoft }}>{i.publico || "—"}</td>
                    {i.calculado && alem ? (
                      <>
                        <td style={{ ...TD, textAlign: "right", fontFamily: MONO, color: i.resultado < i.meta ? T.risco : T.verde }}>{num(i.resultado, i.casas)}%</td>
                        <td style={{ ...TD, textAlign: "right", fontFamily: MONO, color: T.inkSoft }}>{num(i.meta, 0)}%</td>
                        <td style={{ ...TD, textAlign: "right", fontFamily: MONO }}>{i.faltam > 0 ? `${fmtInt(i.faltam)} mulheres` : "na meta"}</td>
                      </>
                    ) : i.calculado ? (
                      <>
                        <td style={{ ...TD, textAlign: "right", fontFamily: MONO }}>{num(i.resultado, i.casas)}</td>
                        <td style={{ ...TD, textAlign: "right", fontFamily: MONO, color: T.inkSoft }}>{num(i.meta, i.casas)}</td>
                        <td style={{ ...TD, textAlign: "center" }}><Nota v={i.nota} /></td>
                        <td style={TD}>
                          {i.ganho > 0 ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ height: 8, borderRadius: 4, background: T.ambar, width: `${Math.max(6, (i.ganho / maxGanho) * 90)}px` }} />
                              <span style={{ fontFamily: MONO, fontSize: 11, color: T.inkSoft }}>{num(i.ganho, 2)}</span>
                            </div>
                          ) : <span style={{ fontSize: 11, color: T.verde }}>na meta</span>}
                        </td>
                      </>
                    ) : (
                      <td colSpan={4} style={{ ...TD, fontSize: 11, color: T.inkSoft }}>{i.motivo}</td>
                    )}
                  </tr>
                  {esta && <tr><td colSpan={alem ? 5 : 7} style={{ padding: 0 }}><Detalhe ind={i} periodo={periodo} /></td></tr>}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function IdssBase({ gestor }) {
  const anoAtual = new Date().getFullYear();
  // Abre no ano corrente: a ANS conta por ano civil, e a lista de quem falta
  // tem de ser "quem ainda não fez NESTE ano" (é o ano que vira a próxima nota).
  const [periodo, setPeriodo] = useState(String(anoAtual));
  const [d, setD] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [atualizando, setAtualizando] = useState(false);
  const [versao, setVersao] = useState(0);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    api.get("/indicadores-ans/base", { params: { periodo } })
      .then((r) => { if (vivo) setD(r.data); })
      .catch((e) => { if (vivo) toast.error(e?.response?.data?.detail || "Falha ao calcular os indicadores do IDSS"); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [periodo, versao]);

  const atualizarCadastros = () => {
    setAtualizando(true);
    api.post("/indicadores-ans/base/atualizar-cadastros")
      .then((r) => { toast.success(`Cadastros do ERP atualizados (${r.data.especialidades} especialidades, ${r.data.planos} planos)`); setVersao((v) => v + 1); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Falha ao ler o ERP"))
      .finally(() => setAtualizando(false));
  };

  const erp = d?.dimensoes_erp;
  const tipErp = erp ? `Do cadastro do ERP: ${erp.especialidades_com_cbo} especialidades com CBO (separa o generalista do especialista) e ${erp.planos} planos, dos quais ${erp.planos_so_odonto} só odontológicos (ficam fora do público).${erp.atualizado_em ? ` Atualizado em ${new Date(erp.atualizado_em).toLocaleString("pt-BR")}.` : ""} Atualiza sozinho toda noite.` : "";

  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b style={{ fontSize: 15, color: T.ink }}>Frente IDSS · nossa base</b>
        <span style={{ fontSize: 12, color: T.inkSoft }}>as regras da ANS aplicadas na nossa carteira, indicador a indicador</span>
        <InfoTip w={400} texto={TIP_FRENTE} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} style={{ padding: "5px 8px", borderRadius: 8, border: `1px solid ${T.line}`, fontSize: 12 }}>
            <option value="12m">Últimos 12 meses (não é o recorte da ANS)</option>
            {[anoAtual, anoAtual - 1, anoAtual - 2].map((a) => <option key={a} value={String(a)}>{a}{a === anoAtual ? " (até hoje)" : ""}</option>)}
          </select>
          {gestor && (
            <Botao pequeno onClick={atualizarCadastros} disabled={atualizando}>
              <RefreshCw size={12} className={atualizando ? "spin" : ""} /> Atualizar cadastros do ERP
            </Botao>
          )}
        </div>
      </div>
      {!d ? (
        <p style={{ fontSize: 13, color: T.inkSoft }}>{carregando ? "Calculando os indicadores na base…" : "Sem dados."}</p>
      ) : (
        <div style={{ opacity: carregando ? 0.6 : 1 }}>
          <p style={{ fontSize: 12, color: T.inkSoft, margin: "6px 0 0", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            Período: {dataBr(d.periodo.ini)} a {dataBr(d.periodo.ref)} · público: beneficiários ativos hoje em planos com consulta ·
            contas médicas até {dataBr(d.ultima_conta)} (as dos últimos meses ainda podem chegar) · regras das fichas técnicas do ano-base {d.ano_fichas}
            <InfoTip w={340} texto={tipErp} />
            <a href={d.fonte_fichas} target="_blank" rel="noreferrer" style={{ color: T.azul, marginLeft: 4 }}>fichas na ANS</a>
          </p>
          {d.nota_ans?.idss !== undefined && d.nota_ans?.idss !== null && (
            <p style={{ fontSize: 13, color: T.ink, margin: "10px 0 0", display: "flex", alignItems: "center", gap: 6 }}>
              IDSS {d.nota_ans.ano} (ano-base {d.nota_ans.ano_base}) da nossa operadora: <Nota v={d.nota_ans.idss} />
              <span style={{ fontSize: 12, color: T.inkSoft }}>(clique numa dimensão para abrir os indicadores)</span>
            </p>
          )}
          {Object.entries(d.dimensoes).map(([k, dim]) => (
            <Dimensao key={k} chave={k} dim={dim} periodo={periodo}
              itens={d.indicadores.filter((i) => i.dimensao === k)} />
          ))}
          {(d.alem_do_idss || []).length > 0 && (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <b style={{ fontSize: 14, color: T.ink }}>Além do IDSS · rastreamentos</b>
                <InfoTip w={360} texto={TIP_ALEM} />
              </div>
              <TabelaDimensao itens={d.alem_do_idss} periodo={periodo} alem />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
